import React, { useEffect, useRef } from 'react';

/**
 * Custom cursor with smooth mouse trail.
 * - Dot follows raw position.
 * - Ring follows with spring delay.
 * - Trail particles emitted at intervals, fading out.
 * Active only on devices with fine pointer (desktop).
 */
export default function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const trailContainerRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(pointer: fine)').matches) return;

    document.documentElement.classList.add('has-custom-cursor');

    const state = {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
      rx: window.innerWidth / 2,
      ry: window.innerHeight / 2,
      hover: false,
      lastTrailAt: 0,
    };

    const onMove = (e) => {
      state.x = e.clientX;
      state.y = e.clientY;
    };

    const onOver = (e) => {
      const target = e.target;
      if (
        target &&
        (target.closest('a, button, [data-cursor-hover], input, textarea, select, label, [role="button"]'))
      ) {
        state.hover = true;
      } else {
        state.hover = false;
      }
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mouseover', onOver, { passive: true });

    let raf;
    const tick = (now) => {
      // Spring towards target for ring.
      const ease = 0.18;
      state.rx += (state.x - state.rx) * ease;
      state.ry += (state.y - state.ry) * ease;

      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${state.x}px, ${state.y}px) translate(-50%, -50%)`;
      }
      if (ringRef.current) {
        const scale = state.hover ? 1.6 : 1;
        ringRef.current.style.transform = `translate(${state.rx}px, ${state.ry}px) translate(-50%, -50%) scale(${scale})`;
        ringRef.current.style.opacity = state.hover ? '0.95' : '0.5';
      }

      // Emit trail particle ~ every 32ms
      if (now - state.lastTrailAt > 30 && trailContainerRef.current) {
        state.lastTrailAt = now;
        const particle = document.createElement('div');
        particle.className = 'cursor-trail';
        particle.style.transform = `translate(${state.x}px, ${state.y}px) translate(-50%, -50%)`;
        particle.style.transition = 'opacity 700ms ease, transform 700ms ease';
        trailContainerRef.current.appendChild(particle);
        requestAnimationFrame(() => {
          particle.style.opacity = '0';
          const drift = (Math.random() - 0.5) * 20;
          particle.style.transform = `translate(${state.x + drift}px, ${state.y + 10}px) translate(-50%, -50%) scale(0.4)`;
        });
        setTimeout(() => particle.remove(), 800);
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseover', onOver);
      document.documentElement.classList.remove('has-custom-cursor');
    };
  }, []);

  return (
    <>
      <div ref={dotRef} className="cursor-dot" />
      <div ref={ringRef} className="cursor-ring" />
      <div ref={trailContainerRef} aria-hidden style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9996 }} />
    </>
  );
}
